(() => {
/**
 * <sneaker-quick-add>
 *
 * Add-to-cart straight from a product card on the collection / search grid,
 * without leaving the page. Only used for single-variant products; multi-variant
 * cards render a plain link to the PDP so options can be chosen (no JS needed).
 *
 * On success it updates + opens Dawn's cart drawer when one is present (via its
 * renderContents API), falls back to Dawn's `cart-update` pub/sub so the header
 * bubble refreshes, and always emits a bubbling `rs:cart:added` CustomEvent so
 * any owner (chrome / cart) can react without this component knowing their guts.
 *
 * @fires rs:cart:added - { bubbles:true, detail:{ items, sections } } on a successful add.
 * @fires rs:cart:error - { bubbles:true, detail:{ message } } when the add fails.
 */
class SneakerQuickAdd extends HTMLElement {
  #controller = null;
  // Must be declared: assigning an undeclared private field is a parse error
  // that used to take the whole file (both custom elements) down.
  #onClick = null;

  connectedCallback() {
    /** @type {HTMLButtonElement|null} */
    this.button = this.querySelector('[data-quick-add-button]');
    // Multi-variant cards render an <a> to the PDP — nothing to enhance.
    if (!this.button || this.button.tagName !== 'BUTTON') return;

    this.#onClick = this.#handleClick.bind(this);
    this.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.button?.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    event.preventDefault();

    const variantId = this.button.dataset.variantId;
    if (!variantId || this.button.getAttribute('aria-disabled') === 'true') return;

    this.#setLoading(true);
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.#sectionsToRender(),
          sections_url: window.location.pathname,
        }),
        signal: this.#controller.signal,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'cart-add-failed');

      this.#syncCart(data);

      this.dispatchEvent(
        new CustomEvent('rs:cart:added', {
          bubbles: true,
          detail: { items: data.items || [data], sections: data.sections },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.dispatchEvent(
        new CustomEvent('rs:cart:error', {
          bubbles: true,
          detail: { message: error.message },
        })
      );
      console.error('[sneaker-quick-add]', error);
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * Update the storefront chrome after an add. Prefer opening the cart drawer if
   * Workstream F shipped one; otherwise fall back to Dawn's pub/sub so at least the
   * header cart bubble refreshes.
   * @param {object} data - The parsed /cart/add.js response (with `sections`).
   */
  #syncCart(data) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      drawer.renderContents(data);
      return;
    }

    if (typeof window.publish === 'function') {
      window.publish('cart-update', {
        source: 'sneaker-quick-add',
        productVariantId: this.button.dataset.variantId,
        cartData: data,
      });
    }
  }

  /**
   * Which theme sections to ask Shopify to re-render on add, so the chrome can
   * swap fresh HTML in. Mirrors what Dawn's cart-drawer expects.
   * @returns {string[]}
   */
  #sectionsToRender() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    if (!this.button) return;
    this.button.setAttribute('aria-disabled', String(loading));
    this.button.classList.toggle('is-loading', loading);
  }
}

if (!customElements.get('sneaker-quick-add')) {
  customElements.define('sneaker-quick-add', SneakerQuickAdd);
}

/**
 * <colourway-swatches>
 *
 * Compact (card-mode) colourway swatch preview. The Liquid snippet renders the
 * sibling colourways as plain anchors (which navigate — the no-JS behaviour);
 * on connect this element upgrades them to buttons so swatch clicks stop
 * navigating AND stop arming the card's view-transition morph (obsidian-motion
 * arms it from any anchor click inside [data-vt-card]).
 *
 * Contract:
 *  - hover/focus on a swatch = image-only preview of that colourway. The swap
 *    assigns srcset before src, decodes off-DOM first (no flash of empty), and
 *    is guarded by a request token so a stale hover can never win over a newer
 *    hover or a commit. Pointer leave / focus out restores the committed image.
 *  - click = commit: swaps the image AND the card's identity (title text,
 *    price markup, media + title link hrefs) and retargets the quick-add
 *    shortcut to the committed colourway (see #retarget), so it acts on the
 *    shown colourway instead of the card's original product. The card carries
 *    `is-colourway-committed` while a sibling is
 *    shown. Clicking the original swatch restores everything.
 *  - while the displayed image differs from the card's own product, the card
 *    carries `is-colourway-alt`, which suppresses the secondary-image hover
 *    cross-fade (that second photo belongs to the original product).
 *
 * Media and price payloads ship per sibling as inert <template> elements
 * (nothing is fetched until previewed). All listeners are element-scoped, so
 * facets AJAX re-renders that re-inject card HTML upgrade cleanly again.
 */
class ColourwaySwatches extends HTMLElement {
  #abort = null;
  #previewToken = 0;
  /** @type {HTMLButtonElement|null} committed sibling button; null = the card's own product */
  #committed = null;
  #upgraded = false;

  connectedCallback() {
    this.card = this.closest('.product-card-sneaker');
    this.img = this.card?.querySelector('.product-card-sneaker__image--primary img');
    // Placeholder cards (no featured image) or unexpected hosts: leave the
    // anchors alone — they keep navigating like before.
    if (!this.card || !this.img) return;

    this.mediaLink = this.card.querySelector('.product-card-sneaker__link');
    this.titleLink = this.card.querySelector('.product-card-sneaker__title-link');
    this.titleText = this.card.querySelector('.product-card-sneaker__title-text');
    this.priceEl = this.card.querySelector('.price-sneaker');
    this.announceEl = this.querySelector('[data-swatch-announce-region]');

    // Action shortcuts that must follow the committed colourway (see #retarget):
    // A single-variant add-to-cart <button> or a multi-variant options <a>.
    this.quickAddHost = this.card.querySelector('sneaker-quick-add.product-card-sneaker__quick-add');
    this.quickAddControl = this.quickAddHost?.querySelector('.product-card-sneaker__quick-add-button');

    // Snapshot the card's own identity so the original swatch can restore it.
    this.original = {
      media: this.#mediaAttrs(this.img),
      title: this.titleText ? this.titleText.textContent : '',
      url: this.titleLink ? this.titleLink.getAttribute('href') : null,
      price: this.priceEl ? this.priceEl.cloneNode(true) : null,
      quickAddHref: this.quickAddControl ? this.quickAddControl.getAttribute('href') : null,
      quickAddVariant: this.quickAddControl ? this.quickAddControl.getAttribute('data-variant-id') : null,
      quickAddProductUrl: this.quickAddHost ? this.quickAddHost.getAttribute('data-product-url') : null,
    };

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    if (!this.#upgraded) {
      this.#upgradeItems();
      this.#upgraded = true;
    }

    for (const btn of this.querySelectorAll('button.colourway-swatches__item')) {
      // pointerenter/leave don't bubble, so these are per-button (still scoped
      // to this element's own subtree and torn down via the AbortController).
      btn.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'touch') return; // touch = tap commits, no hover state
          this.#preview(btn);
        },
        { signal }
      );
      btn.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.#endPreview();
        },
        { signal }
      );
    }

    this.addEventListener(
      'focusin',
      (event) => {
        const btn = event.target.closest?.('button.colourway-swatches__item');
        if (btn) this.#preview(btn);
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.#endPreview();
      },
      { signal }
    );
    this.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest?.('button.colourway-swatches__item');
        if (btn) this.#commit(btn);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * Swap the Liquid-rendered anchors (and the current-product span) for
   * buttons: same classes, same children, same data-* payload references.
   */
  #upgradeItems() {
    for (const el of this.querySelectorAll('a.colourway-swatches__item, span.colourway-swatches__item')) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'colourway-swatches__item';
      for (const [key, value] of Object.entries(el.dataset)) btn.dataset[key] = value;
      const isCurrent = el.dataset.swatchCurrent === 'true';
      btn.setAttribute('aria-pressed', String(isCurrent));
      // State now lives on aria-pressed; normalise the accessible name to the
      // colourway title (the static "(current colourway)" copy is the no-JS text).
      while (el.firstChild) btn.appendChild(el.firstChild);
      const label = btn.querySelector('.visually-hidden');
      if (label && btn.dataset.swatchTitle) label.textContent = btn.dataset.swatchTitle;
      el.replaceWith(btn);
    }
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {{src: string|null, srcset: string|null, sizes: string|null, width: string|null, height: string|null, alt: string}}
   */
  #mediaAttrs(img) {
    return {
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset'),
      sizes: img.getAttribute('sizes'),
      width: img.getAttribute('width'),
      height: img.getAttribute('height'),
      alt: img.getAttribute('alt') || '',
    };
  }

  /**
   * @param {HTMLButtonElement} btn
   * @returns {object|null} media attrs for that swatch, null when it has no image payload
   */
  #mediaFor(btn) {
    if (btn.dataset.swatchCurrent === 'true') return this.original.media;
    const template = btn.closest('li')?.querySelector('template[data-swatch-media]');
    const img = template?.content.querySelector('img');
    return img ? this.#mediaAttrs(img) : null;
  }

  /**
   * Decode off-DOM, then swap the live card image — srcset before src, so the
   * browser never kicks off a fetch for the fallback src alone. The token
   * makes a stale (slower) hover lose against anything newer.
   * @param {object|null} media
   * @param {number} token
   */
  async #applyMedia(media, token) {
    if (!media || !media.src) return;
    const probe = new Image();
    if (media.srcset) probe.srcset = media.srcset;
    if (media.sizes) probe.sizes = media.sizes;
    probe.src = media.src;
    try {
      await probe.decode();
    } catch (error) {
      // decode() can reject (e.g. detached before load) — swap anyway, the
      // browser paints it as soon as it arrives.
    }
    if (token !== this.#previewToken) return;

    const img = this.img;
    if (media.srcset) img.setAttribute('srcset', media.srcset);
    else img.removeAttribute('srcset');
    if (media.sizes) img.setAttribute('sizes', media.sizes);
    else img.removeAttribute('sizes');
    img.setAttribute('src', media.src);
    if (media.width) img.setAttribute('width', media.width);
    if (media.height) img.setAttribute('height', media.height);
    img.setAttribute('alt', media.alt);
  }

  /**
   * Keep `is-colourway-alt` on the card exactly while the displayed image is
   * NOT the card's own product (previewing or committed to a sibling).
   * @param {HTMLButtonElement|null} shownBtn - null = the card's own product
   */
  #syncAltClass(shownBtn) {
    const isAlt = Boolean(shownBtn) && shownBtn.dataset.swatchCurrent !== 'true';
    this.card.classList.toggle('is-colourway-alt', isAlt);
  }

  /** @param {HTMLButtonElement} btn */
  #preview(btn) {
    const token = ++this.#previewToken;
    this.#applyMedia(this.#mediaFor(btn), token);
    this.#syncAltClass(btn);
  }

  /** Restore whatever is committed (a sibling, or the card's own product). */
  #endPreview() {
    const token = ++this.#previewToken;
    const committed = this.#committed;
    this.#applyMedia(committed ? this.#mediaFor(committed) : this.original.media, token);
    this.#syncAltClass(committed);
  }

  /** @param {HTMLButtonElement} btn */
  #commit(btn) {
    const isOriginal = btn.dataset.swatchCurrent === 'true';
    const token = ++this.#previewToken;
    this.#applyMedia(isOriginal ? this.original.media : this.#mediaFor(btn), token);

    const url = isOriginal ? this.original.url : btn.dataset.swatchUrl;
    if (url) {
      if (this.mediaLink) this.mediaLink.setAttribute('href', url);
      if (this.titleLink) this.titleLink.setAttribute('href', url);
    }
    if (this.titleText) {
      this.titleText.textContent = isOriginal ? this.original.title : btn.dataset.swatchTitle || this.original.title;
    }
    this.#swapPrice(isOriginal ? null : btn);

    this.#committed = isOriginal ? null : btn;
    this.card.classList.toggle('is-colourway-committed', !isOriginal);
    this.#syncAltClass(this.#committed);
    this.#retarget(isOriginal ? null : btn);

    for (const b of this.querySelectorAll('button.colourway-swatches__item')) {
      b.setAttribute('aria-pressed', String(b === btn));
    }
    if (this.announceEl && btn.dataset.swatchAnnounce) {
      this.announceEl.textContent = btn.dataset.swatchAnnounce;
    }
  }

  /**
   * Point the quick-add shortcut at the committed colourway, or restore the
   * card's own product when `btn` is null.
   * @param {HTMLButtonElement|null} btn - the committed sibling, or null for the original
   */
  #retarget(btn) {
    if (this.quickAddControl) {
      if (this.quickAddControl.tagName === 'A') {
        // Options link: retarget to the colourway's product page.
        const href = btn ? btn.dataset.swatchUrl || this.original.quickAddHref : this.original.quickAddHref;
        if (href) this.quickAddControl.setAttribute('href', href);
      } else {
        // Single-variant add-to-cart button: it must add the SHOWN colourway's
        // own variant, never the card's original. A committed sibling with no
        // usable variant (e.g. sold out → empty data-swatch-variant-id) has no
        // safe target, so hide the button for that commit rather than fall back
        // to the original variant and add the wrong item. Restoring the base
        // colourway always shows it again.
        const variant = btn ? btn.dataset.swatchVariantId : this.original.quickAddVariant;
        if (btn && !variant) {
          this.quickAddHost.hidden = true;
        } else {
          if (variant) this.quickAddControl.setAttribute('data-variant-id', variant);
          const url = btn ? btn.dataset.swatchUrl || this.original.quickAddProductUrl : this.original.quickAddProductUrl;
          if (url) this.quickAddHost.setAttribute('data-product-url', url);
          this.quickAddHost.hidden = false;
        }
      }
    }
  }

  /** @param {HTMLButtonElement|null} btn - null restores the original price */
  #swapPrice(btn) {
    if (!this.priceEl) return;
    let next = null;
    if (!btn) {
      next = this.original.price ? this.original.price.cloneNode(true) : null;
    } else {
      const template = btn.closest('li')?.querySelector('template[data-swatch-price]');
      const price = template?.content.querySelector('.price-sneaker');
      next = price ? price.cloneNode(true) : null;
    }
    if (!next) return;
    this.priceEl.replaceWith(next);
    this.priceEl = next;
  }
}

if (!customElements.get('colourway-swatches')) {
  customElements.define('colourway-swatches', ColourwaySwatches);
}
})();
